import React, { useState, useEffect } from 'react';
import { Form } from 'reactstrap';
import { ApiRequest, ButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ModalButtonBuilder } from '../../../../../../../../GlobalComponents/Builders';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';
import { useForm } from 'react-hook-form';

/**
 * CleanupActionsModal — Renders the enhanced "Manage Job" cleanup section
 * with granular delete options for key mappings, value mappings, custom keys, and setup values.
 *
 * Dropdowns show counts per resource pair/resource and include orphaned items.
 */
const CleanupActionsModal = ({ jobData, jobResources, jobResourceMappings, handleDMTAction, getEverything }) => {

    const [cleanupInfo, setCleanupInfo] = useState(null);
    const { confirm, ConfirmModal } = useConfirmModal();

    const fetchCleanupInfo = () => {
        ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId: jobData.id, actionName: 'cleanup-info' },
            body: {},
        }).then(results => {
                if (results?.data?.success) {
                    setCleanupInfo(results.data);
                }
            });
    };

    // Fetch cleanup info on mount
    useEffect(() => { fetchCleanupInfo(); }, []);

    const totals = cleanupInfo?.totals || {};

    /**
     * Build resource pair options with counts for key mappings.
     */
    const buildKeyPairOptions = () => {
        const mappings = jobResourceMappings?.current || [];
        const pairCounts = cleanupInfo?.keyPairCounts || {};
        const orphanedPairs = cleanupInfo?.orphanedKeyPairs || [];
        const options = [];

        mappings.forEach(m => {
            const pairStr = `${m.source_resource_id}-${m.destination_resource_id}`;
            const counts = pairCounts[pairStr];
            if (counts && counts.total) {
                options.push({
                    label: `${m.source_resource_name} → ${m.destination_resource_name}`,
                    description: `${counts.total} total, ${counts.mapped} mapped, ${counts.inUse} in use`,
                    value: pairStr,
                    _source: m.source_resource_name,
                    _dest: m.destination_resource_name,
                });
            }
        });

        options.sort((a, b) => a._source.localeCompare(b._source) || a._dest.localeCompare(b._dest));

        // Orphaned entries (resource mapping deleted)
        orphanedPairs.forEach(o => {
            options.push({
                label: `${o.label}`,
                description: `${o.count} orphaned`,
                value: 'orphaned',
            });
        });

        return options;
    };

    /**
     * Build resource pair options with counts for value mappings.
     */
    const buildValuePairOptions = () => {
        const mappings = jobResourceMappings?.current || [];
        const pairCounts = cleanupInfo?.valuePairCounts || {};
        const orphanedPairs = cleanupInfo?.orphanedValuePairs || [];
        const options = [];

        mappings.forEach(m => {
            const pairStr = `${m.source_resource_id}-${m.destination_resource_id}`;
            const counts = pairCounts[pairStr];
            if (counts && counts.total) {
                options.push({
                    label: `${m.source_resource_name} → ${m.destination_resource_name}`,
                    description: `${counts.total} total, ${counts.mapped} mapped, ${counts.inUse} in use`,
                    value: pairStr,
                    _source: m.source_resource_name,
                    _dest: m.destination_resource_name,
                });
            }
        });

        options.sort((a, b) => a._source.localeCompare(b._source) || a._dest.localeCompare(b._dest));

        orphanedPairs.forEach(o => {
            options.push({
                label: `${o.label}`,
                description: `${o.count} orphaned`,
                value: 'orphaned',
            });
        });

        return options;
    };

    /**
     * Format option label for two-line display in react-select dropdowns.
     */
    const formatPairOptionLabel = (option) => (
        <div>
            <div style={{ fontWeight: 500 }}>{option.label}</div>
            {option.description && <div style={{ fontSize: 11, color: '#999' }}>{option.description}</div>}
        </div>
    );

    /**
     * Build resource options with counts for custom keys (grouped by resource + connection).
     */
    const buildCustomKeyResourceOptions = () => {
        const resourceCounts = cleanupInfo?.customKeyResourceCounts || {};
        const orphanedResources = cleanupInfo?.orphanedCustomKeyResources || [];
        const options = [];
        const seen = new Set();

        const sourceConnectionId = jobData?.source_connection?.id;

        if (jobResources) {
            Object.entries(jobResources).forEach(([connectionId, connection]) => {
                if (Array.isArray(connection.selected_resources)) {
                    const side = String(connectionId) === String(sourceConnectionId) ? 'Source' : 'Dest';
                    connection.selected_resources.forEach(r => {
                        const key = `${r.resource_id}-${connectionId}`;
                        if (resourceCounts[key]) {
                            options.push({
                                label: `${r.name || r.resource} (${side}) — ${resourceCounts[key]}`,
                                value: key,
                                _side: side,
                                _name: r.name || r.resource,
                            });
                            seen.add(key);
                        }
                    });
                }
            });
        }

        // Sort: Source first, then Dest, then alphabetical by name
        options.sort((a, b) => {
            if (a._side !== b._side) return a._side === 'Source' ? -1 : 1;
            return a._name.localeCompare(b._name);
        });

        orphanedResources.forEach(o => {
            const key = `${o.resource_id}-${o.connection_id}`;
            if (!seen.has(key)) {
                options.push({
                    label: `Resource ${o.resource_id} — orphaned (${o.count})`,
                    value: key,
                });
            }
        });

        return options;
    };

    /**
     * Build resource options with counts for setup values (grouped by resource + connection).
     */
    const buildSetupValueResourceOptions = () => {
        const resourceCounts = cleanupInfo?.setupValueResourceCounts || {};
        const orphanedResources = cleanupInfo?.orphanedValueResources || [];
        const options = [];
        const seen = new Set();

        const sourceConnectionId = jobData?.source_connection?.id;

        if (jobResources) {
            Object.entries(jobResources).forEach(([connectionId, connection]) => {
                if (Array.isArray(connection.selected_resources)) {
                    const side = String(connectionId) === String(sourceConnectionId) ? 'Source' : 'Dest';
                    connection.selected_resources.forEach(r => {
                        const key = `${r.resource_id}-${connectionId}`;
                        if (resourceCounts[key]) {
                            options.push({
                                label: `${r.name || r.resource} (${side}) — ${resourceCounts[key]}`,
                                value: key,
                                _side: side,
                                _name: r.name || r.resource,
                            });
                            seen.add(key);
                        }
                    });
                }
            });
        }

        // Sort: Source first, then Dest, then alphabetical by name
        options.sort((a, b) => {
            if (a._side !== b._side) return a._side === 'Source' ? -1 : 1;
            return a._name.localeCompare(b._name);
        });

        orphanedResources.forEach(o => {
            const key = `${o.resource_id}-${o.connection_id}`;
            if (!seen.has(key)) {
                options.push({
                    label: `Resource ${o.resource_id} — orphaned (${o.count})`,
                    value: key,
                });
            }
        });

        return options;
    };

    // ─── Delete Key Mappings (Granular) ───
    const KeyMappingsCleanup = () => {
        const form = useForm({ defaultValues: { scope: 'selected' } });
        const { handleSubmit, watch } = form;
        const scope = watch('scope');
        const selectedPairs = watch('resourcePairs');

        const getSelectedPairsSummary = (pairCounts) => {
            if (!selectedPairs || !Array.isArray(selectedPairs) || selectedPairs.length === 0) return null;
            let total = 0, mapped = 0, inUse = 0;
            selectedPairs.forEach(p => {
                const key = p.value || p;
                const counts = pairCounts[key];
                if (counts) { total += counts.total; mapped += counts.mapped; inUse += counts.inUse; }
            });
            return `Total ${total} mappings, ${mapped} mapped, ${inUse} in use`;
        };

        const pairCounts = cleanupInfo?.keyPairCounts || {};
        const summary = getSelectedPairsSummary(pairCounts);

        const onSubmit = async (formData) => {
            const payload = { scope: formData.scope };
            let scopeDesc;
            if (formData.scope === 'selected') {
                const pairs = formData.resourcePairs;
                if (!pairs || (Array.isArray(pairs) && pairs.length === 0)) {
                    return ToastBuilder('error', 'Please select at least one resource pair');
                }
                payload.resourcePairs = Array.isArray(pairs) ? pairs.map(p => p.value || p) : [pairs.value || pairs];
                scopeDesc = `the ${payload.resourcePairs.length} selected resource pair(s)`;
            } else {
                scopeDesc = `ALL ${totals.keyMappings || 0} key mapping(s)`;
            }
            const confirmed = await confirm(`Delete key mappings for ${scopeDesc}? This cannot be undone.`,
                { title: 'Delete Key Mappings', confirmLabel: 'Delete' });
            if (!confirmed) return;
            handleDMTAction('delete-key-mappings-v2', null, () => {
                ToastBuilder('success', 'Key mappings deleted');
                getEverything();
                fetchCleanupInfo();
            }, payload);
        };

        return (
            <Form id='cleanup-key-mappings-form' onSubmit={handleSubmit(onSubmit)}>
                <p style={{ marginBottom: 12 }}>Delete key mappings for this job. Select all or narrow down by resource pair.</p>
                <RegisteredInputsBuilder
                    form={form}
                    inputs={[
                        {
                            label: 'Scope',
                            type: 'reactselect',
                            name: 'scope',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            options: [
                                { label: 'Per Resource Pair', value: 'selected' },
                                { label: `All Key Mappings (${totals.keyMappings || 0})`, value: 'all' },
                            ],
                        },
                        {
                            label: 'Resource Pairs',
                            type: 'reactselect',
                            name: 'resourcePairs',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            visible: scope === 'selected',
                            isMulti: true,
                            options: buildKeyPairOptions(),
                            placeholder: 'Select resource pairs...',
                            helpText: summary,
                            settings: { formatOptionLabel: formatPairOptionLabel },
                        },
                    ]}
                />
                <div className='text-end mt-3'>
                    <ButtonBuilder label='Delete Key Mappings' className='btn-danger' type='submit' />
                </div>
            </Form>
        );
    };

    // ─── Delete Value Mappings (Enhanced) ───
    const ValueMappingsCleanup = () => {
        const form = useForm({ defaultValues: { scope: 'selected' } });
        const { handleSubmit, watch } = form;
        const scope = watch('scope');
        const selectedValuePairs = watch('resourcePairs');

        const getSelectedValuePairsSummary = () => {
            const pairCounts = cleanupInfo?.valuePairCounts || {};
            if (!selectedValuePairs || !Array.isArray(selectedValuePairs) || selectedValuePairs.length === 0) return null;
            let total = 0, mapped = 0, inUse = 0;
            selectedValuePairs.forEach(p => {
                const key = p.value || p;
                const counts = pairCounts[key];
                if (counts) { total += counts.total; mapped += counts.mapped; inUse += counts.inUse; }
            });
            return `Total ${total} mappings, ${mapped} mapped, ${inUse} in use`;
        };

        const valueSummary = getSelectedValuePairsSummary();

        const onSubmit = async (formData) => {
            const payload = { scope: formData.scope };
            let scopeDesc;
            if (formData.scope === 'selected') {
                const pairs = formData.resourcePairs;
                if (!pairs || (Array.isArray(pairs) && pairs.length === 0)) {
                    return ToastBuilder('error', 'Please select at least one resource pair');
                }
                payload.resourcePairs = Array.isArray(pairs) ? pairs.map(p => p.value || p) : [pairs.value || pairs];
                scopeDesc = `the ${payload.resourcePairs.length} selected resource pair(s)`;
            } else {
                scopeDesc = `ALL ${totals.valueMappings || 0} value mapping(s)`;
            }
            const confirmed = await confirm(`Delete value mappings for ${scopeDesc}? This cannot be undone.`,
                { title: 'Delete Value Mappings', confirmLabel: 'Delete' });
            if (!confirmed) return;
            handleDMTAction('delete-value-mappings-v2', null, () => {
                ToastBuilder('success', 'Value mappings deleted');
                getEverything();
                fetchCleanupInfo();
            }, payload);
        };

        return (
            <Form id='cleanup-value-mappings-form' onSubmit={handleSubmit(onSubmit)}>
                <p style={{ marginBottom: 12 }}>Delete value mappings for this job. Value mappings are leaf nodes and can always be safely deleted.</p>
                <RegisteredInputsBuilder
                    form={form}
                    inputs={[
                        {
                            label: 'Scope',
                            type: 'reactselect',
                            name: 'scope',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            options: [
                                { label: 'Per Resource Pair', value: 'selected' },
                                { label: `All Value Mappings (${totals.valueMappings || 0})`, value: 'all' },
                            ],
                        },
                        {
                            label: 'Resource Pairs',
                            type: 'reactselect',
                            name: 'resourcePairs',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            visible: scope === 'selected',
                            isMulti: true,
                            options: buildValuePairOptions(),
                            placeholder: 'Select resource pairs...',
                            helpText: valueSummary,
                            settings: { formatOptionLabel: formatPairOptionLabel },
                        },
                    ]}
                />
                <div className='text-end mt-3'>
                    <ButtonBuilder label='Delete Value Mappings' className='btn-danger' type='submit' />
                </div>
            </Form>
        );
    };

    // ─── Delete Custom Keys ───
    const CustomKeysCleanup = () => {
        const form = useForm({ defaultValues: { scope: 'selected' } });
        const { handleSubmit, watch } = form;
        const scope = watch('scope');

        const onSubmit = async (formData) => {
            const payload = { scope: formData.scope };
            let scopeDesc;
            if (formData.scope === 'selected') {
                const ids = formData.resourceIds;
                if (!ids || (Array.isArray(ids) && ids.length === 0)) {
                    return ToastBuilder('error', 'Please select at least one resource');
                }
                payload.resourceIds = Array.isArray(ids) ? ids.map(r => r.value || r) : [ids.value || ids];
                scopeDesc = `the ${payload.resourceIds.length} selected resource(s)`;
            } else {
                scopeDesc = `ALL ${totals.customKeys || 0} custom key(s)`;
            }
            const confirmed = await confirm(`Delete unmapped custom keys for ${scopeDesc}? Keys in use are skipped. This cannot be undone.`,
                { title: 'Delete Custom Keys', confirmLabel: 'Delete' });
            if (!confirmed) return;
            handleDMTAction('delete-custom-keys', null, () => {
                ToastBuilder('success', 'Custom keys deleted');
                getEverything();
                fetchCleanupInfo();
            }, payload);
        };

        return (
            <Form id='cleanup-custom-keys-form' onSubmit={handleSubmit(onSubmit)}>
                <p style={{ marginBottom: 8 }}>Delete custom keys created for this job that are <strong>not</strong> referenced in any key mapping.</p>
                <p className='dmt-cleanup-hint' style={{ marginLeft: 0, marginBottom: 12 }}>Keys that are in use (mapped) will be skipped automatically.</p>
                <RegisteredInputsBuilder
                    form={form}
                    inputs={[
                        {
                            label: 'Scope',
                            type: 'reactselect',
                            name: 'scope',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            options: [
                                { label: 'Per Resource', value: 'selected' },
                                { label: `All Resources (${totals.customKeys || 0} keys)`, value: 'all' },
                            ],
                        },
                        {
                            label: 'Resources',
                            type: 'reactselect',
                            name: 'resourceIds',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            visible: scope === 'selected',
                            isMulti: true,
                            options: buildCustomKeyResourceOptions(),
                            placeholder: 'Select resources...',
                        },
                    ]}
                />
                <div className='text-end mt-3'>
                    <ButtonBuilder label='Delete Custom Keys' className='btn-danger' type='submit' />
                </div>
            </Form>
        );
    };

    // ─── Delete Setup Values ───
    const SetupValuesCleanup = () => {
        const form = useForm({ defaultValues: { scope: 'selected', deletePairedMappings: false } });
        const { handleSubmit, watch } = form;
        const scope = watch('scope');

        const isSyncJob = jobData?.is_sync && parseInt(jobData?.sync_job_id) > 0;

        const onSubmit = async (formData) => {
            const payload = { scope: formData.scope };
            let scopeDesc;
            if (formData.scope === 'selected') {
                const ids = formData.resourceIds;
                if (!ids || (Array.isArray(ids) && ids.length === 0)) {
                    return ToastBuilder('error', 'Please select at least one resource');
                }
                payload.resourceIds = Array.isArray(ids) ? ids.map(r => r.value || r) : [ids.value || ids];
                scopeDesc = `the ${payload.resourceIds.length} selected resource(s)`;
            } else {
                scopeDesc = `ALL ${totals.setupValues || 0} setup value(s)`;
            }

            const pairedNote = (isSyncJob && formData.deletePairedMappings)
                ? ` Value mappings on the paired Job ${jobData?.sync_job_id} will also be removed.` : '';
            const confirmed = await confirm(`Delete unmapped setup values for ${scopeDesc}? Values in use are skipped.${pairedNote} This cannot be undone.`,
                { title: 'Delete Setup Values', confirmLabel: 'Delete' });
            if (!confirmed) return;

            if (isSyncJob) {
                payload.deletePairedMappings = !!formData.deletePairedMappings;
                handleDMTAction('delete-sync-setup-values', null, (results) => {
                    if (results?.data?.requiresConfirmation) {
                        ToastBuilder('error', results.data.message);
                    } else {
                        ToastBuilder('success', results?.data?.message || 'Setup values deleted');
                        getEverything();
                        fetchCleanupInfo();
                    }
                }, payload);
            } else {
                handleDMTAction('delete-setup-values', null, () => {
                    ToastBuilder('success', 'Setup values deleted');
                    getEverything();
                    fetchCleanupInfo();
                }, payload);
            }
        };

        return (
            <Form id='cleanup-setup-values-form' onSubmit={handleSubmit(onSubmit)}>
                {isSyncJob ? (
                    <>
                        <p style={{ marginBottom: 8 }}>Delete setup values for this sync job.</p>
                        <p className='dmt-cleanup-hint' style={{ marginLeft: 0, marginBottom: 12 }}>
                            ⚠️ This job is a two-way sync (paired with Job {jobData.sync_job_id}).
                            Source values on this job are used as destination values on the paired job.
                            Deleting them will orphan value mappings on the paired job unless you also remove those.
                        </p>
                    </>
                ) : (
                    <>
                        <p style={{ marginBottom: 8 }}>Delete setup values for this job that are <strong>not</strong> referenced in any value mapping.</p>
                        <p className='dmt-cleanup-hint' style={{ marginLeft: 0, marginBottom: 12 }}>Values that are in use (mapped) will be skipped automatically.</p>
                    </>
                )}
                <RegisteredInputsBuilder
                    form={form}
                    inputs={[
                        {
                            label: 'Scope',
                            type: 'reactselect',
                            name: 'scope',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            options: [
                                { label: 'Per Resource', value: 'selected' },
                                { label: `All Resources (${totals.setupValues || 0} values)`, value: 'all' },
                            ],
                        },
                        {
                            label: 'Resources',
                            type: 'reactselect',
                            name: 'resourceIds',
                            size: 12,
                            labelSize: 3,
                            inputSize: 9,
                            visible: scope === 'selected',
                            isMulti: true,
                            options: buildSetupValueResourceOptions(),
                            placeholder: 'Select resources...',
                        },
                        {
                            label: 'Also delete value mappings on paired job',
                            type: 'checkbox',
                            name: 'deletePairedMappings',
                            size: 12,
                            labelSize: 11,
                            inputSize: 1,
                            visible: isSyncJob,
                        },
                    ]}
                />
                {isSyncJob && (
                    <p className='dmt-cleanup-hint' style={{ marginLeft: 0, marginTop: 4, marginBottom: 0 }}>
                        This will remove value mappings on Job {jobData?.sync_job_id} that reference these values.
                    </p>
                )}
                <div className='text-end mt-3'>
                    <ButtonBuilder label={isSyncJob ? 'Delete Sync Values' : 'Delete Setup Values'} className='btn-danger' type='submit' />
                </div>
            </Form>
        );
    };

    // ─── Main Render ───
    return (
        <div className='dmt-cleanup-actions'>
            {/* Key Mappings */}
            <div className='dmt-cleanup-row'>
                <div className='d-flex justify-content-between align-items-center'>
                    <div>
                        <span style={{ fontWeight: 600 }}>Key Mappings</span>
                        <span className='dmt-cleanup-hint'>{cleanupInfo ? `${totals.keyMappings || 0} mapping(s)` : 'Loading...'}</span>
                    </div>
                    <ModalButtonBuilder
                        title='Manage'
                        modalTitle='Delete Key Mappings'
                        className='btn-outline-danger btn-sm'
                        color={null}
                        size='md'
                        onSubmit={false}
                        onCancelLabel='Close'
                        body={<KeyMappingsCleanup />}
                    />
                </div>
            </div>

            {/* Value Mappings */}
            <div className='dmt-cleanup-row'>
                <div className='d-flex justify-content-between align-items-center'>
                    <div>
                        <span style={{ fontWeight: 600 }}>Value Mappings</span>
                        <span className='dmt-cleanup-hint'>{cleanupInfo ? `${totals.valueMappings || 0} mapping(s)` : 'Loading...'}</span>
                    </div>
                    <ModalButtonBuilder
                        title='Manage'
                        modalTitle='Delete Value Mappings'
                        className='btn-outline-danger btn-sm'
                        color={null}
                        size='md'
                        onSubmit={false}
                        onCancelLabel='Close'
                        body={<ValueMappingsCleanup />}
                    />
                </div>
            </div>

            {/* Custom Keys */}
            <div className='dmt-cleanup-row'>
                <div className='d-flex justify-content-between align-items-center'>
                    <div>
                        <span style={{ fontWeight: 600 }}>Custom Keys</span>
                        <span className='dmt-cleanup-hint'>{cleanupInfo ? `${totals.customKeys || 0} key(s)` : 'Loading...'}</span>
                    </div>
                    <ModalButtonBuilder
                        title='Manage'
                        modalTitle='Delete Custom Keys'
                        className='btn-outline-danger btn-sm'
                        color={null}
                        size='md'
                        onSubmit={false}
                        onCancelLabel='Close'
                        body={<CustomKeysCleanup />}
                    />
                </div>
            </div>

            {/* Setup Values */}
            <div className='dmt-cleanup-row'>
                <div className='d-flex justify-content-between align-items-center'>
                    <div>
                        <span style={{ fontWeight: 600 }}>Setup Values</span>
                        <span className='dmt-cleanup-hint'>{cleanupInfo ? `${totals.setupValues || 0} value(s)` : 'Loading...'}</span>
                    </div>
                    <ModalButtonBuilder
                        title='Manage'
                        modalTitle='Delete Setup Values'
                        className='btn-outline-danger btn-sm'
                        color={null}
                        size='md'
                        onSubmit={false}
                        onCancelLabel='Close'
                        body={<SetupValuesCleanup />}
                    />
                </div>
            </div>

            <ConfirmModal />
        </div>
    );
};

export default CleanupActionsModal;
